// google-signin — wrapper do Google Identity Services (GIS) pra obter um
// id_token no NAVEGADOR MOBILE. Parede A1 (login mobile).
//
// FLUXO: carregamos o script https://accounts.google.com/gsi/client uma vez,
// inicializamos com o VITE_GOOGLE_OAUTH_CLIENT_ID e disparamos o prompt. O GIS
// devolve uma `credential` (que É o JWT id_token), que mandamos pro backend
// (POST /v1/swipe-account/oauth/google → verifyIdToken). O backend já valida a
// audience contra o MESMO GOOGLE_OAUTH_CLIENT_ID.
//
// 🟡 DÚVIDA PRO TECH-LEAD (fluxo Google no webview/mobile):
//   1. VITE_GOOGLE_OAUTH_CLIENT_ID precisa ser provisionado no BUILD do app
//      (Vite é build-time). Hoje o app TV NÃO passa esse build-arg — sem ele o
//      botão Google fica desabilitado (isGoogleSignInEnabled()===false) e o
//      email/senha cobre 100% do login. Confirmar se usamos o MESMO client ID
//      do site (AUTH_GOOGLE_ID == GOOGLE_OAUTH_CLIENT_ID) e adicionar a origin
//      do app mobile às "Authorized JavaScript origins" no console do Google.
//   2. Em WEBVIEW puro (não navegador), o GIS pode ser bloqueado pelo Google
//      ("disallowed_useragent"). Em navegador mobile (Chrome/Safari) funciona.
//      Se o alvo for webview embarcado, talvez precise do fluxo de redirect
//      OAuth no navegador externo. Por isso entregamos o motor + botão prontos,
//      mas o fluxo GIS só LIGA com client ID presente — sem inventar um fluxo
//      OAuth quebrado. Tech-lead decide ligar/ajustar.

const GSI_SRC = 'https://accounts.google.com/gsi/client'

/** Client ID do Google (build-time, Vite). Vazio = botão Google desabilitado. */
function getGoogleClientId(): string {
  try {
    return (import.meta.env['VITE_GOOGLE_OAUTH_CLIENT_ID'] as string | undefined) ?? ''
  } catch {
    return ''
  }
}

/**
 * O login com Google está disponível? (client ID provisionado no build E ambiente
 * de browser). A UI usa pra mostrar o botão habilitado vs uma dica de
 * "indisponível" — nunca um botão que clica e não faz nada.
 */
export function isGoogleSignInEnabled(): boolean {
  return getGoogleClientId() !== '' && typeof window !== 'undefined'
}

// ===== Tipagem mínima do GIS (só o que usamos) =====
interface GoogleCredentialResponse {
  credential: string // o id_token (JWT)
}
interface GoogleAccountsId {
  initialize(config: {
    client_id: string
    callback: (resp: GoogleCredentialResponse) => void
    auto_select?: boolean
    cancel_on_tap_outside?: boolean
  }): void
  prompt(listener?: (notification: GooglePromptNotification) => void): void
}
interface GooglePromptNotification {
  isNotDisplayed(): boolean
  isSkippedMoment(): boolean
  isDismissedMoment(): boolean
  getNotDisplayedReason?(): string
}
interface GoogleGlobal {
  accounts: { id: GoogleAccountsId }
}

function getGoogle(): GoogleGlobal | undefined {
  return (globalThis as { google?: GoogleGlobal }).google
}

let scriptPromise: Promise<void> | null = null

/** Carrega o script do GIS uma vez (idempotente). Rejeita se falhar o load. */
function loadGsiScript(): Promise<void> {
  if (getGoogle()?.accounts?.id) return Promise.resolve()
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise<void>((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('no_document'))
      return
    }
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('gsi_load_failed')))
      return
    }
    const script = document.createElement('script')
    script.src = GSI_SRC
    script.async = true
    script.defer = true
    script.addEventListener('load', () => resolve())
    script.addEventListener('error', () => {
      scriptPromise = null // permite tentar de novo numa próxima interação
      reject(new Error('gsi_load_failed'))
    })
    document.head.appendChild(script)
  })
  return scriptPromise
}

export type GoogleSignInError = 'unavailable' | 'load_failed' | 'dismissed'

export type GoogleSignInResult =
  | { ok: true; idToken: string }
  | { ok: false; error: GoogleSignInError }

/**
 * Dispara o fluxo do Google e resolve com o id_token (credential). Em qualquer
 * falha resolve com { ok:false, error } — nunca lança (a UI trata os 3 casos).
 *  - unavailable: client ID ausente (botão não devia ter clicado).
 *  - load_failed: script do GIS não carregou (rede/CSP/webview bloqueado).
 *  - dismissed: usuário fechou o prompt sem escolher conta.
 */
export async function signInWithGoogle(): Promise<GoogleSignInResult> {
  if (!isGoogleSignInEnabled()) return { ok: false, error: 'unavailable' }
  const clientId = getGoogleClientId()

  try {
    await loadGsiScript()
  } catch {
    return { ok: false, error: 'load_failed' }
  }
  const google = getGoogle()
  if (!google?.accounts?.id) return { ok: false, error: 'load_failed' }

  return new Promise<GoogleSignInResult>((resolve) => {
    let settled = false
    const finish = (r: GoogleSignInResult): void => {
      if (settled) return
      settled = true
      resolve(r)
    }
    try {
      google.accounts.id.initialize({
        client_id: clientId,
        callback: (resp) => {
          if (resp?.credential) finish({ ok: true, idToken: resp.credential })
          else finish({ ok: false, error: 'dismissed' })
        },
        cancel_on_tap_outside: true,
      })
      google.accounts.id.prompt((notification) => {
        // Prompt não exibido / pulado / dispensado sem credential → tratamos como
        // dismissed (o callback acima resolve no caminho feliz).
        if (
          notification.isNotDisplayed?.() ||
          notification.isSkippedMoment?.() ||
          notification.isDismissedMoment?.()
        ) {
          finish({ ok: false, error: 'dismissed' })
        }
      })
    } catch {
      finish({ ok: false, error: 'load_failed' })
    }
  })
}
